/**
 * Admin API client
 * All admin-related API functions for the admin portal
 */

import { authenticatedFetch } from './auth';
import type { BalanceScheduleView, SavedCard } from './studios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// =============================================================================
// Types - Shows
// =============================================================================

export interface Show {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  default_base_price_cents: number;
  is_active: boolean;
  created_at: string;
  tour_stops?: { count: number }[];
  class_options?: ClassOption[];
}

export interface ClassOption {
  id: string;
  show_id: string;
  name: string;
  duration_minutes: number;
  price_cents: number;
  description: string | null;
  max_participants: number | null;
  is_active: boolean;
}

export interface CreateShowInput {
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  default_base_price_cents: number;
  is_active?: boolean;
}

export interface UpdateShowInput {
  name?: string;
  description?: string | null;
  logo_url?: string | null;
  default_base_price_cents?: number;
  is_active?: boolean;
}

// =============================================================================
// Types - Theaters & Cities
// =============================================================================

export interface Theater {
  id: string;
  name: string;
  city_id: string;
  address: string;
  zip_code?: string | null;
  latitude: number | null;
  longitude: number | null;
  capacity: number | null;
  is_active?: boolean;
  city?: City;
}

export interface City {
  id: string;
  name: string;
  state: string;
  country?: string;
  timezone?: string;
  is_active?: boolean;
}

export interface CreateTheaterInput {
  name: string;
  city_id: string;
  address: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  capacity?: number;
}

export interface CreateCityInput {
  name: string;
  state: string;
  timezone?: string;
  /** Google Places place_id (locality) — lets the backend dedup to a canonical row. */
  google_place_id?: string;
  /** ISO 3166-1 alpha-2 country code from the Places picker (e.g. "US", "CA"). */
  country?: string;
  /** Latitude from the Places picker — stored as the city's PostGIS location. */
  latitude?: number;
  /** Longitude from the Places picker — stored as the city's PostGIS location. */
  longitude?: number;
}

// =============================================================================
// Types - Tour Stops
// =============================================================================

export interface TourStop {
  id: string;
  show_id: string;
  theater_id: string;
  city_id: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_sold_out: boolean;
  notes: string | null;
  matinee_time: string | null;
  max_faculty: number;
  sunday_enabled: boolean;
  monday_enabled: boolean;
  sunday_matinee_time: string | null;
  monday_start_time: string | null;
  created_at: string;
  show?: Show;
  theater?: Theater;
  city?: City;
  booking_count?: number;
  class_count?: number;
  waitlist_count?: number;
  date_view_mode?: 'auto' | 'list' | 'calendar';
  blocked_dates?: string[];
  is_forced_closed?: boolean;
}

export interface CreateTourStopInput {
  show_id: string;
  theater_id: string;
  city_id: string;
  start_date: string;
  end_date: string;
  is_active?: boolean;
  notes?: string;
  date_view_mode?: 'auto' | 'list' | 'calendar';
  matinee_time?: string | null;
  max_faculty?: number;
  sunday_enabled?: boolean;
  monday_enabled?: boolean;
  sunday_matinee_time?: string | null;
  monday_start_time?: string | null;
  blocked_dates?: string[];
  is_forced_closed?: boolean;
}

export interface UpdateTourStopInput {
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  is_sold_out?: boolean;
  notes?: string | null;
  date_view_mode?: 'auto' | 'list' | 'calendar';
  matinee_time?: string | null;
  max_faculty?: number;
  sunday_enabled?: boolean;
  monday_enabled?: boolean;
  sunday_matinee_time?: string | null;
  monday_start_time?: string | null;
  blocked_dates?: string[];
  is_forced_closed?: boolean;
}

export interface CloneTourStopInput {
  start_date: string;
  end_date: string;
}

// =============================================================================
// Types - Bookings
// =============================================================================

export interface AdminTimelineEntry {
  id: string;
  action: string;
  description: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// SDA-266: faculty payout rows attached to the booking detail response.
export interface BookingFacultyPayout {
  id: string;
  class_id: string | null;
  amount_cents: number;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled';
  scheduled_for: string | null;
  processed_at: string | null;
  failure_reason: string | null;
  retry_count: number;
  stripe_transfer_id: string | null;
  payment_type: string;
  faculty_name: string;
}

// SDA-266: server-computed preview of what Mark Complete will schedule.
export interface BookingPayoutPreviewItem {
  class_id: string;
  class_date: string;
  faculty_name: string;
  amount_cents: number;
  stripe_ready: boolean;
}

/** SDA-296: how much of one class's share of the balance the studio has paid. */
export interface BookingClassPayment {
  class_id: string;
  share_cents: number;
  paid_cents: number;
}

export interface AdminBooking {
  id: string;
  studio_id: string;
  tour_stop_id: string;
  status: 'pending' | 'deposit_paid' | 'confirmed' | 'balance_paid' | 'completed' | 'cancelled' | 'refunded';
  total_amount_cents: number;
  deposit_amount_cents: number;
  balance_due_cents: number;
  marketing_materials: boolean;
  group_ticket_inquiry?: boolean;
  terms_accepted_at: string;
  created_at: string;
  // Pricing breakdown
  total_cents?: number; // Alias for total_amount_cents
  subtotal_cents?: number;
  travel_surcharge_cents?: number;
  marketing_fee_cents?: number;
  // Real booking-detail GET columns (deposit_amount_cents is only an alias on
  // the LIST endpoint). SDA-232 uses these to detect an unpaid deposit remainder.
  deposit_cents?: number;
  deposit_paid_cents?: number;
  // Balance tracking
  balance_paid_cents?: number;
  balance_status?: string | null;
  balance_due_date?: string | null;
  // Payment timestamps
  deposit_paid_at?: string | null;
  balance_paid_at?: string | null;
  // Cancellation info
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  // Booking number
  booking_number: string | null;
  // Session scheduling
  start_time_preference?: 'early' | 'default' | 'late';
  simultaneous_sessions?: boolean;
  simultaneous_trigger?: 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null;
  // Marketing materials completion
  marketing_materials_completed_at?: string | null;
  marketing_materials_completed_by?: 'admin' | 'studio' | null;
  marketing_materials_last_reminder_at?: string | null;
  // SDA-263 fulfillment statuses — drive the dashboard request queues.
  marketing_request_status?: 'waiting_studio_material' | 'in_progress' | 'complete';
  group_ticket_status?: 'new' | 'waiting_ticket_order' | 'complete';
  marketing_logo_url?: string | null;
  marketing_signup_method?: string | null;
  marketing_class_cost?: string | null;
  // Related inquiry
  inquiry_id?: string | null;
  studio?: {
    id: string;
    name: string;
    city: string;
    state: string;
    address_line1?: string;
    address_line2?: string | null;
    zip_code?: string;
    /** SDA-265: true when the studio has never activated its account. */
    is_dormant?: boolean;
    primary_contact?: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
    };
  };
  tour_stop?: TourStop;
  classes?: AdminClass[];
  payments?: AdminPayment[];
  notes?: AdminNote[];
  timeline?: AdminTimelineEntry[];
  // SDA-266: payout close-the-loop data (booking detail GET only)
  faculty_payouts?: BookingFacultyPayout[];
  payout_preview?: BookingPayoutPreviewItem[];
  next_payout_date?: string | null;
  /** Per-class auto-charge schedule; null unless classes are charged on different dates. */
  balance_schedule?: BalanceScheduleView | null;
  /** SDA-296: per-class paid vs share; present (never null) on multi-class bookings. */
  class_payments?: BookingClassPayment[] | null;
}

export interface AdminClass {
  id: string;
  booking_id: string;
  class_option_id: string;
  faculty_id: string | null;
  faculty_assignment_status?: 'pending' | 'accepted' | 'declined' | null;
  scheduled_date: string;
  time_slot: string;
  start_time: string | null;
  end_time: string | null;
  // The classes.status DB enum (this used to list booking-style values that never occur).
  status: 'scheduled' | 'faculty_assigned' | 'ready' | 'in_progress' | 'completed' | 'cancelled';
  student_count: number | null;
  expected_students: number | null;
  student_level: string | null;
  class_level?: string | null;
  special_requests: string | null;
  // SDA-266: whether the faculty transfer for this class has succeeded
  faculty_paid?: boolean;
  // SDA-276: when faculty_paid was set (Stripe transfer OR marked paid externally)
  faculty_paid_at?: string | null;
  // Pricing
  price_cents?: number;
  class_option?: ClassOption;
  faculty?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email?: string | null;
    headshot_url: string | null;
  };
  // Related tour stop info (populated from booking)
  tour_stop?: {
    id: string;
    show?: Show;
    theater?: Theater;
    city?: City;
  };
}

export interface AdminPayment {
  id: string;
  booking_id: string;
  amount_cents: number;
  /** Cumulative cents refunded against this payment (webhook + admin refund converge here). */
  refunded_cents?: number | null;
  payment_type: 'deposit' | 'balance' | 'refund';
  // Alias for payment_type (some pages use 'type' instead)
  type?: 'deposit' | 'balance' | 'refund';
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'partially_refunded';
  stripe_payment_intent_id: string | null;
  stripe_refund_id?: string | null;
  created_at: string;
}

export interface AdminNote {
  id: string;
  booking_id: string;
  user_id: string;
  note: string;
  created_at: string;
  user?: {
    first_name: string;
    last_name: string;
  };
}

export interface BookingFilters {
  status?: string;
  show_id?: string;
  studio_id?: string;
  tour_stop_id?: string;
  start_date?: string;
  end_date?: string;
  faculty_assignment?: 'assigned' | 'unassigned';
  created_start?: string;
  created_end?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CreateManualBookingInput {
  studio_id: string;
  tour_stop_id: string;
  classes: {
    class_option_id: string;
    scheduled_date: string;
    // Pass session_identifier (Sat/Sun), time_slot (Monday legacy key), or start_time (Monday exact 24h).
    session_identifier?: 'session_1' | 'session_2';
    time_slot?: string;
    /** Exact 24h start time for Monday bookings (HH:MM:SS) */
    start_time?: string;
    student_count?: number;
    student_level?: string;
    special_requests?: string;
  }[];
  marketing_materials?: boolean;
  group_ticket_inquiry?: boolean;
  notes?: string;
  // Session structure — mirrors the public booking flow.
  simultaneous_sessions?: boolean;
  simultaneous_trigger?: 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null;
  start_time_preference?: 'early' | 'default' | 'late';
  /** Defaults to 'confirmed' server-side. 'deposit_request' creates the booking Pending and emails a deposit-payment link to the studio's primary contact. */
  payment_mode?: 'confirmed' | 'deposit_request';
}

export interface UpdateBookingInput {
  status?: string;
  notes?: string;
  start_time_preference?: 'early' | 'default' | 'late';
  simultaneous_sessions?: boolean;
  simultaneous_trigger?: 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null;
  marketing_materials_completed?: boolean;
  group_ticket_inquiry?: boolean;
  marketing_request_status?: 'waiting_studio_material' | 'in_progress' | 'complete';
  group_ticket_status?: 'new' | 'waiting_ticket_order' | 'complete';
}

export interface UpdateClassInput {
  class_date?: string;
  start_time?: string;
  end_time?: string;
  class_level?: 'all_levels' | 'beginner' | 'intermediate' | 'advanced';
  expected_students?: number;
}

export interface RefundInput {
  amount_cents: number;
  reason: string;
}

export interface RefundResult {
  total_refunded_cents: number;
  refunds: Array<{
    payment_id: string;
    stripe_refund_id: string;
    amount_cents: number;
  }>;
}

export interface CancelBookingInput {
  reason: string;
  refund_deposit?: boolean;
  notify_studio?: boolean;
}

// =============================================================================
// Types - Inquiries
// =============================================================================

export interface AdminInquiry {
  id: string;
  studio_id: string | null;
  tour_stop_id: string | null;
  show_id: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  studio_name: string | null;
  inquiry_type: 'general' | 'hosted' | 'remote';
  message: string;
  status: 'pending' | 'in_review' | 'responded' | 'converted' | 'closed';
  assigned_to: string | null;
  response_due_at: string | null;
  responded_at: string | null;
  sla_breached_at: string | null;
  booking_id: string | null;
  tracking_token: string;
  created_at: string;
  updated_at: string;

  // Computed per-row by GET /api/admin/inquiries
  sla_status?: 'on_track' | 'warning' | 'breached';
  age_hours?: number;

  // Additional inquiry context
  inquiry_source?: 'dual_option_hosted' | 'remote_request' | 'general' | 'website' | string;
  city?: City;
  preferred_dates?: string[];
  venue_details?: {
    address?: string;
    capacity?: number;
    parking_info?: string;
    // Alias for parking_info
    parking?: string;
  };

  // Related data
  studio?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zip_code?: string | null;
    primary_contact?: {
      first_name: string;
      last_name: string;
      // Computed property for display
      name?: string;
      email: string;
      phone: string | null;
    };
  };
  tour_stop?: TourStop;
  show?: Show;
  assigned_user?: {
    id: string;
    first_name: string;
    last_name: string;
  };

  linked_booking?: {
    id: string;
    booking_number: string;
    status: string;
  };

  // Response and activity history
  responses?: {
    id: string;
    content?: string;
    // Alias for content
    body?: string;
    subject?: string;
    sent_at: string;
    sent_by: {
      first_name: string;
      last_name: string;
    };
    // Alias for sent_by
    admin_user?: {
      id: string;
      first_name: string;
      last_name: string;
      name?: string;
    };
    included_booking_link?: boolean;
  }[];
  activity_log?: {
    id: string;
    action: string;
    details: string | null;
    // Alias for details
    description?: string | null;
    created_at: string;
    user?: {
      first_name: string;
      last_name: string;
      name?: string;
    };
  }[];
}

export interface InquiryFilters {
  status?: string;
  show_id?: string;
  city_id?: string;
  inquiry_type?: string;
  assigned_to?: string;
  sla_status?: 'on_track' | 'warning' | 'breached';
  source?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface UpdateInquiryInput {
  status?: string;
  assigned_to?: string | null;
}

export interface RespondToInquiryInput {
  response?: string;
  // Alias for response (some pages use 'body')
  body?: string;
  subject?: string;
  template_id?: string;
  include_booking_link?: boolean;
}

export interface ConvertInquiryInput {
  tour_stop_id: string;
  class_option_id: string;
  class_date: string;
  time_slot: string;
  participant_count: number;
  class_level: string;
  skip_deposit?: boolean;
}

// =============================================================================
// Types - Faculty
// =============================================================================

export interface FacultyPayout {
  id: string;
  faculty_id: string;
  class_id: string;
  amount_cents: number;
  status: 'pending' | 'processing' | 'paid' | 'failed';
  stripe_transfer_id: string | null;
  paid_at: string | null;
  created_at: string;
  class_info?: {
    scheduled_date: string;
    time_slot: string;
    show_name: string;
    studio_name: string;
  };
  // Alias for class_info (some pages use 'class')
  class?: {
    scheduled_date: string;
    time_slot: string;
    show_name: string;
    studio_name: string;
    // Nested references
    show?: Show;
    studio?: {
      id: string;
      name: string;
      city: string;
      state: string;
    };
  };
}

export interface FacultyStats {
  total: number;
  active: number;
  pending_approval: number;
  onboarding: number;
  invited: number;
  inactive: number;
}

export interface AdminFaculty {
  id: string;
  user_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  status: 'invited' | 'onboarding' | 'pending_approval' | 'active' | 'inactive';
  headshot_url: string | null;
  bio: string | null;
  stripe_account_id: string | null;
  stripe_account_status?: string | null;
  stripe_connected?: boolean;
  stripe_onboarding_complete?: boolean;
  // Note: W-9 tax document collection is handled by Stripe Connect Express
  default_rate_cents: number;
  invitation_token: string | null;
  invitation_expires_at: string | null;
  // Set once the invitee completes the accept flow — the source of truth for
  // whether resend-invitation is still valid (status/user_id can drift, e.g.
  // a legacy orphaned row, or be linked before acceptance for admin-created
  // faculty; accepted_at reflects the invitation's lifecycle exactly).
  invitation_accepted_at?: string | null;
  created_at: string;
  // Returned after creation
  invitation_url?: string;
  email_sent?: boolean;
  faculty_shows?: {
    show_id?: string;
    shows?: Show;
    // Whether this certification is active (faculty assignable to the show). SDA-118.
    is_active?: boolean;
  }[];
  assignment_count?: number;

  // Nested user data from Supabase join
  users?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;

  // Computed/aliased properties for page compatibility
  name?: string;
  // Flattened certified shows. `cert_active` carries the faculty_shows.is_active
  // flag (distinct from Show.is_active, which is the show's own state). SDA-118.
  shows?: (Show & { cert_active?: boolean })[];
  pay_rate_cents?: number;
  upcoming_assignments?: number;
  stripe_connect_id?: string | null;
  stripe_connect_status?: 'enabled' | 'restricted' | 'pending' | null;

  // Broadway credits for faculty bio
  broadway_credits?: {
    show: string;
    role: string;
    year?: string;
  }[];

  // Availability management
  availability_blocks?: {
    id: string;
    start_date: string;
    end_date: string;
    reason?: string;
  }[];

  // Additional data that may be included from API
  assignments?: ClassAssignment[];
  // NOTE: payout history is NOT returned by the detail endpoint — the admin
  // Payouts tab loads it via getAdminFacultyPayouts({ faculty_id }) (SDA-221).
}

export interface FacultyFilters {
  status?: string;
  // SDA-215: widen the assignment dropdown to all non-deactivated faculty
  // (active/invited/onboarding/pending_approval). Ignored when `status` is set.
  assignable?: boolean;
  show_id?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  exclude_date?: string;
  exclude_time?: string;
  exclude_class_id?: string;
  exclude_faculty_id?: string;
}

export interface CreateFacultyInput {
  first_name: string;
  last_name: string;
  email: string;
  default_rate_cents: number;
  show_ids: string[];
}

export interface UpdateFacultyInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  bio?: string | null;
  status?: string;
  default_rate_cents?: number | null;
  show_ids?: string[];
}

export interface AssignFacultyInput {
  class_id: string;
}

export interface ClassAssignment {
  id: string;
  booking_id: string;
  faculty_id: string;
  scheduled_date: string;
  /** Raw TIME value from DB (e.g. '10:00:00'). Render via formatTimeDisplay from @sda/shared. */
  start_time: string | null;
  end_time: string | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  student_count: number;
  student_level: string;
  special_requests: string | null;
  created_at: string;

  // Direct reference shortcuts (flattened from nested objects)
  show?: Show;
  city?: City;
  studio?: {
    id: string;
    name: string;
    city: string;
    state: string;
  };

  // Related data (nested)
  booking?: {
    id: string;
    studio?: {
      id: string;
      name: string;
      city: string;
      state: string;
    };
  };
  tour_stop?: {
    id: string;
    show?: Show;
    theater?: {
      id: string;
      name: string;
      city: string;
      state: string;
    };
  };
}

// =============================================================================
// Types - Campaigns (Phase 4.1)
// =============================================================================

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  campaign_type: 'announcement' | 'show_nearby' | 'reminder' | 'promotion';
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
  target_show_id: string | null;
  target_city_id: string | null;
  target_radius_miles: number | null;
  scheduled_at: string | null;
  sent_at: string | null;
  recipient_count: number;
  opens_count: number;
  clicks_count: number;
  bounces_count: number;
  created_at: string;
  show?: Show;
  city?: City;
}

// =============================================================================
// Types - Coupons
// =============================================================================

export interface Coupon {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  applies_to: 'total' | 'sessions_only' | 'addons_only';
  max_uses: number | null;
  single_use_per_studio: boolean;
  expiry_date: string | null;
  is_active: boolean;
  internal_notes: string | null;
  created_at: string;
  updated_at: string;
  usage_count?: number;
}

export interface CouponUsage {
  id: string;
  coupon_id: string;
  booking_id: string;
  studio_id: string;
  discount_cents: number;
  is_cancelled: boolean;
  created_at: string;
  studio_name?: string;
  booking_number?: string;
}

export interface CouponDetail extends Coupon {
  usages: CouponUsage[];
}

export interface CreateCouponInput {
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  applies_to: 'total' | 'sessions_only' | 'addons_only';
  max_uses: number | null;
  single_use_per_studio: boolean;
  expiry_date: string | null;
  is_active: boolean;
  internal_notes: string | null;
}

export type UpdateCouponInput = Omit<CreateCouponInput, 'code'>;

// =============================================================================
// Types - Dashboard
// =============================================================================

export interface RecentBooking {
  id: string;
  studio_name: string;
  show_name: string;
  status: string;
  created_at: string;
  /** Earliest class date (YYYY-MM-DD) across all classes for this booking, or null if none. */
  class_date: string | null;
  needs_faculty?: boolean;
  /** Number of classes (sessions) in this booking. */
  class_count: number;
}

/**
 * A booking carrying an outstanding marketing-material or group-ticket request
 * (SDA-263). Oldest-first rows for the two dashboard fulfillment queues — the
 * worker orders by created_at ascending (SDA-286) and the panel renders payload
 * order verbatim, so the aging pill column reads high to low.
 */
export interface DashboardRequestRow {
  id: string;
  booking_number: string;
  studio_name: string;
  show_name: string;
  /** When the booking was made — drives the aging pill. */
  created_at: string;
  /**
   * Earliest class date (YYYY-MM-DD) across the booking's classes, or null if no
   * classes are scheduled yet. Display only: it fills the row's "Class <date>"
   * sub-line. It was the queue's sort key under SDA-263 (soonest class first);
   * SDA-286 replaced that with oldest request first, leaving this date as the
   * tie-break for rows booked in the same instant.
   */
  class_date: string | null;
  /**
   * Marketing-only: when SDA last nudged the studio to submit their materials.
   * Always null on group-ticket rows (there is no reminder for those).
   */
  last_reminder_at?: string | null;
  /**
   * Unified fulfillment status for the row's category (marketing_request_status
   * or group_ticket_status). Optional so rows from a stale worker render
   * without a badge.
   */
  request_status?: string | null;
}

export interface AdminDashboardStats {
  // Flat stats for dashboard cards
  active_shows: number;
  upcoming_classes: number;
  pending_inquiries: number;
  /** Total contract value sold MTD (SDA-246): SUM of bookings.total_cents for committed bookings created this month. */
  revenue_mtd_cents: number;
  /** Actual Stripe charges MTD net of refunds (SDA-246). */
  cash_mtd_cents: number;
  /** Class sessions on bookings created this month with a committed status (SDA-233). */
  classes_sold_mtd: number;

  // SLA tracking
  sla_breached_count: number;
  sla_warning_count: number;

  // Recent activity
  recent_bookings: RecentBooking[];

  /**
   * SDA-263 fulfillment queues. Optional so a stale worker (deployed before this
   * change) leaves the panels empty instead of blanking the dashboard.
   *
   * Both lists carry every request whose fulfillment status is not yet
   * 'complete'; marking a request Complete on the booking page clears its row
   * on the next dashboard load.
   */
  open_marketing_requests?: DashboardRequestRow[];
  /** Exact total of open marketing requests; the array above is capped at 10. */
  open_marketing_count?: number;
  open_group_ticket_requests?: DashboardRequestRow[];
  /** Exact total of group ticket requests; the array above is capped at 10. */
  open_group_ticket_count?: number;

  // Waitlist
  waitlist_signups: number;
  recent_waitlist: {
    id: string;
    name: string | null;
    email: string;
    position: number;
    created_at: string;
    tour_stops: {
      start_date: string;
      end_date: string;
      shows: { name: string };
      cities: { name: string; state: string };
    };
  }[];

  // Structured stats (for detail views)
  bookings?: {
    total: number;
    pending: number;
    confirmed: number;
    this_month: number;
  };
  revenue?: {
    total_cents: number;
    this_month_cents: number;
    pending_cents: number;
  };
  inquiries?: {
    total: number;
    pending: number;
    overdue: number;
  };
  faculty?: {
    total: number;
    active: number;
    pending_approval: number;
  };
  upcoming_classes_detail?: {
    id: string;
    scheduled_date: string;
    time_slot: string;
    show_name: string;
    studio_name: string;
    faculty_name: string | null;
    status: string;
  }[];
}

// =============================================================================
// Types - Calendar
// =============================================================================

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  type: 'tour_stop' | 'class';
  show_id: string;
  show_name: string;
  show_color?: string;
  tour_stop_id?: string;
  class_id?: string;
  studio_name?: string;
  faculty_name?: string;
  is_sold_out?: boolean;
  booking_count?: number;
}

export interface CalendarFilters {
  start_date: string;
  end_date: string;
  show_id?: string;
  event_type?: 'tour_stop' | 'class' | 'all';
}

export interface CalendarTourStop {
  id: string;
  show_id: string;
  start_date: string;
  end_date: string;
  is_sold_out: boolean;
  shows: { id: string; name: string; slug: string } | null;
  theaters: { id: string; name: string } | null;
  cities: { id: string; name: string; state: string } | null;
}

export interface CalendarClass {
  id: string;
  tour_stop_id: string;
  class_date: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  class_level: string | null;
  expected_students: number | null;
  faculty: { id: string; first_name: string; last_name: string } | null;
  bookings: {
    id: string;
    booking_number: string;
    status: string;
    studios: { id: string; name: string } | null;
  } | null;
}

export interface CalendarData {
  tourStops: CalendarTourStop[];
  classes: CalendarClass[];
}

// =============================================================================
// Types - System Settings
// =============================================================================

export interface SystemSettings {
  id: string;
  default_matinee_time: string;
  matinee_threshold_time: string;
  default_session_start_time: string;
  session_duration_minutes: number;
  admin_notification_email: string;
  inquiry_notification_email: string;
  campaign_auto_queue_days_before: number;
  campaign_cadence_warning_days: number;
  campaign_reminder_interval_days: number;
  campaign_final_reminder_days_before: number;
  campaign_daily_send_limit: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateSystemSettingsInput {
  default_matinee_time?: string;
  matinee_threshold_time?: string;
  default_session_start_time?: string;
  session_duration_minutes?: number;
  admin_notification_email?: string;
  inquiry_notification_email?: string;
  campaign_auto_queue_days_before?: number;
  campaign_cadence_warning_days?: number;
  campaign_reminder_interval_days?: number;
  campaign_final_reminder_days_before?: number;
  campaign_daily_send_limit?: number;
}

// =============================================================================
// API Functions - System Settings
// =============================================================================

export async function getSystemSettings(): Promise<SystemSettings> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/settings`);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to fetch settings');
  return result.data;
}

export async function updateSystemSettings(input: UpdateSystemSettingsInput): Promise<SystemSettings> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to update settings');
  return result.data;
}

// =============================================================================
// API Functions - Dashboard
// =============================================================================

export async function getAdminDashboard(): Promise<AdminDashboardStats> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/dashboard`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch dashboard');
  }

  return result.data;
}

// =============================================================================
// API Functions - Waitlist
// =============================================================================

export interface WaitlistEntry {
  id: string;
  tour_stop_id: string;
  name: string | null;
  email: string;
  position: number;
  created_at: string;
  notified_at: string | null;
  converted_at: string | null;
  archived_at: string | null;
  archived?: boolean;
  status: 'pending' | 'notified' | 'converted';
  show_name: string | null;
  city_name: string | null;
  city_state: string | null;
  tour_stop_label: string | null;
  studio_name: string | null;
  tour_stops: { start_date: string; end_date: string } | null;
}

export interface WaitlistResponse {
  data: WaitlistEntry[];
  pagination: { page: number; per_page: number; total: number };
  summary: { total: number; by_show: { show_name: string; count: number }[] };
}

export async function getAdminWaitlist(params?: {
  tour_stop_id?: string;
  show_id?: string;
  status?: string;
  page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<WaitlistResponse> {
  const qp = new URLSearchParams();
  if (params?.tour_stop_id) qp.append('tour_stop_id', params.tour_stop_id);
  if (params?.show_id) qp.append('show_id', params.show_id);
  if (params?.status) qp.append('status', params.status);
  if (params?.page) qp.append('page', String(params.page));
  if (params?.sort_by) qp.append('sort_by', params.sort_by);
  if (params?.sort_order) qp.append('sort_order', params.sort_order);

  const response = await authenticatedFetch(`${API_BASE}/api/admin/waitlist?${qp}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch waitlist');
  }

  return result.data;
}

/**
 * SDA-226: reversibly archive a resolved waitlist entry (hidden from the default
 * list, restorable from the Archived filter). No email/booking side effects.
 */
export async function archiveWaitlistEntry(id: string): Promise<WaitlistEntry> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/waitlist/${id}/archive`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to archive waitlist entry');
  }

  return result.data;
}

/**
 * SDA-226: restore an archived waitlist entry back to the active list.
 */
export async function unarchiveWaitlistEntry(id: string): Promise<WaitlistEntry> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/waitlist/${id}/unarchive`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to restore waitlist entry');
  }

  return result.data;
}

// =============================================================================
// API Functions - Shows
// =============================================================================

export async function getShows(): Promise<Show[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch shows');
  }

  return result.data;
}

export async function getShow(showId: string): Promise<Show> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows/${showId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch show');
  }

  return result.data;
}

export async function createShow(input: CreateShowInput): Promise<Show> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create show');
  }

  return result.data;
}

export async function updateShow(showId: string, input: UpdateShowInput): Promise<Show> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows/${showId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update show');
  }

  return result.data;
}

export async function uploadShowLogo(showId: string, file: File): Promise<{ logo_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows/${showId}/logo`, {
    method: 'POST',
    body: formData,
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to upload logo');
  }

  return result.data;
}

export async function createClassOption(
  showId: string,
  input: {
    name: string;
    duration_minutes: number;
    price_cents: number;
    description?: string;
    max_participants?: number;
  }
): Promise<ClassOption> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/shows/${showId}/class-options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create class option');
  }

  return result.data;
}

export async function updateClassOption(
  optionId: string,
  input: {
    name?: string;
    duration_minutes?: number;
    price_cents?: number;
    description?: string | null;
    max_participants?: number | null;
    is_active?: boolean;
  }
): Promise<ClassOption> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/class-options/${optionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update class option');
  }

  return result.data;
}

// =============================================================================
// API Functions - Theaters & Cities
// =============================================================================

export async function getTheaters(): Promise<Theater[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/theaters`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch theaters');
  }

  return result.data;
}

export async function updateTheater(id: string, data: {
  name?: string;
  address?: string;
  zip_code?: string;
  city_id?: string;
  capacity?: number;
  is_active?: boolean;
}): Promise<Theater> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/theaters/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update theater');
  }

  return result.data;
}

export async function createTheater(input: CreateTheaterInput): Promise<Theater> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/theaters`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create theater');
  }

  return result.data;
}

export async function deleteTheater(id: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/theaters/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    let message: string;
    try {
      const result = await response.json() as { error?: { message?: string } };
      message = result.error?.message ||
        (response.status === 409 ? "Can't delete: this theater is in use" : 'Failed to delete theater');
    } catch {
      message = response.status === 409
        ? "Can't delete: this theater is in use"
        : 'Failed to delete theater';
    }
    throw new Error(message);
  }
}

export async function getCities(): Promise<City[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/cities`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch cities');
  }

  return result.data;
}

export async function createCity(input: CreateCityInput): Promise<City> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/cities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create city');
  }

  return result.data;
}

export interface UpdateCityInput {
  name?: string;
  state?: string;
  timezone?: string;
  /** Google Places place_id (locality) — update the canonical key when re-picking. */
  google_place_id?: string;
  /** ISO 3166-1 alpha-2 country code from the Places picker (e.g. "US", "CA"). */
  country?: string;
  /** Latitude from the Places picker — updates the city's PostGIS location. */
  latitude?: number;
  /** Longitude from the Places picker — updates the city's PostGIS location. */
  longitude?: number;
}

export async function updateCity(id: string, data: UpdateCityInput): Promise<City> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/cities/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update city');
  }
  return result.data;
}

// =============================================================================
// API Functions - Tour Stops
// =============================================================================

export async function getTourStops(filters?: {
  show_id?: string;
  city_id?: string;
  is_active?: boolean;
  is_sold_out?: boolean;
  // SDA-178: semantic time filter — backend resolves 'now' for timezone consistency
  time_filter?: 'upcoming' | 'past' | 'all';
  page?: number;
  limit?: number;
  sort_by?: 'start_date' | 'end_date' | 'is_active' | 'is_sold_out' | 'created_at' | 'show_name' | 'city_name' | 'bookings_count';
  sort_order?: 'asc' | 'desc';
}): Promise<{ tour_stops: TourStop[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.show_id) params.append('show_id', filters.show_id);
  if (filters?.city_id) params.append('city_id', filters.city_id);
  if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
  if (filters?.is_sold_out !== undefined) params.append('is_sold_out', String(filters.is_sold_out));
  if (filters?.time_filter) params.append('time_filter', filters.time_filter);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);

  const url = params.toString()
    ? `${API_BASE}/api/admin/tour-stops?${params}`
    : `${API_BASE}/api/admin/tour-stops`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch tour stops');
  }

  return result.data;
}

type TourStopFiltersWithoutPagination = Omit<
  Parameters<typeof getTourStops>[0],
  'page' | 'limit'
>;

export async function getAllTourStops(
  filters?: TourStopFiltersWithoutPagination
): Promise<{ tour_stops: TourStop[]; total: number }> {
  const firstPage = await getTourStops({ ...filters, page: 1, limit: 100 });
  const { tour_stops, total } = firstPage;

  if (total <= tour_stops.length) {
    return firstPage;
  }

  const totalPages = Math.ceil(total / 100);
  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      getTourStops({ ...filters, page: i + 2, limit: 100 })
    )
  );

  return {
    tour_stops: [
      ...tour_stops,
      ...remainingPages.flatMap((page) => page.tour_stops),
    ],
    total,
  };
}

export async function getTourStop(tourStopId: string): Promise<TourStop> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch tour stop');
  }

  return result.data;
}

export async function createTourStop(input: CreateTourStopInput): Promise<TourStop> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create tour stop');
  }

  return result.data;
}

export async function updateTourStop(
  tourStopId: string,
  input: UpdateTourStopInput
): Promise<TourStop & { cancelled_campaigns?: Array<{ id: string; name: string }> }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update tour stop');
  }

  return result.data;
}

/** Reads a response body as JSON, or throws the plain "server unavailable" message when it isn't JSON. */
async function readJsonOrUnavailable<T>(response: Response): Promise<T> {
  try {
    return await response.json() as T;
  } catch {
    throw new Error(
      `The server is temporarily unavailable (HTTP ${response.status}). Please try again in a minute.`
    );
  }
}

export async function deleteTourStop(tourStopId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}`, {
    method: 'DELETE',
  });
  const result = await readJsonOrUnavailable<{ success?: boolean; error?: { message?: string } }>(response);

  if (!response.ok || !result.success) {
    throw new Error(result.error?.message || 'Failed to delete tour stop');
  }
}

export interface UnsentCampaign {
  id: string;
  name: string;
  status: 'pending_approval' | 'scheduled' | 'sending';
  send_at: string | null;
  estimated_audience: number | null;
}

/** Campaigns linked to a tour stop that haven't finished sending (cancelled if the stop is deactivated). */
export async function getTourStopUnsentCampaigns(tourStopId: string): Promise<UnsentCampaign[]> {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/tour-stops/${encodeURIComponent(tourStopId)}/unsent-campaigns`
  );
  const result = await readJsonOrUnavailable<{
    success?: boolean;
    data?: { campaigns: UnsentCampaign[] };
    error?: { message?: string };
  }>(response);

  if (!response.ok || !result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to check this tour stop for scheduled emails');
  }

  return result.data.campaigns;
}

export async function cloneTourStop(tourStopId: string, input: CloneTourStopInput): Promise<TourStop> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to clone tour stop');
  }

  return result.data;
}

export async function markTourStopSoldOut(tourStopId: string, isSoldOut: boolean): Promise<TourStop> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}/sold-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_sold_out: isSoldOut }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update sold out status');
  }

  return result.data;
}

// =============================================================================
// API Functions - Tour Stop Faculty
// =============================================================================

export interface TourStopFacultyDate {
  date: string;
  status: 'available' | 'blocked' | 'assigned';
  assigned_to?: { booking_number: string; studio_name: string };
}

export interface TourStopFacultyMember {
  id: string;
  name: string;
  headshot_url: string | null;
  stripe_account_status: string | null;
  dates: TourStopFacultyDate[];
}

export interface TourStopFacultyResponse {
  faculty: TourStopFacultyMember[];
  summary: { total_certified: number; available_today: number };
}

export async function getTourStopFaculty(tourStopId: string): Promise<TourStopFacultyResponse> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/tour-stops/${tourStopId}/faculty`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch tour stop faculty');
  }

  return result.data;
}

// =============================================================================
// API Functions - Calendar
// =============================================================================

export async function getCalendarEvents(filters: CalendarFilters): Promise<CalendarEvent[]> {
  const params = new URLSearchParams();
  params.append('start_date', filters.start_date);
  params.append('end_date', filters.end_date);
  if (filters.show_id) params.append('show_id', filters.show_id);
  if (filters.event_type) params.append('event_type', filters.event_type);

  const response = await authenticatedFetch(`${API_BASE}/api/admin/calendar?${params}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch calendar events');
  }

  return result.data;
}

export async function getCalendarData(filters: {
  start_date: string;
  end_date: string;
  show_id?: string;
}): Promise<CalendarData> {
  const params = new URLSearchParams();
  params.append('start_date', filters.start_date);
  params.append('end_date', filters.end_date);
  if (filters.show_id) params.append('show_id', filters.show_id);

  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/calendar?${params}`,
  );
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch calendar data');
  }

  return result.data;
}

// =============================================================================
// API Functions - Bookings
// =============================================================================

// search_truncated: the studio-name search matched more studios than the backend can
// filter on at once, so `bookings` is an arbitrary subset of the real match set.
export async function getAdminBookings(filters?: BookingFilters): Promise<{ bookings: AdminBooking[]; total: number; search_truncated?: boolean }> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.show_id) params.append('show_id', filters.show_id);
  if (filters?.studio_id) params.append('studio_id', filters.studio_id);
  if (filters?.tour_stop_id) params.append('tour_stop_id', filters.tour_stop_id);
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  if (filters?.faculty_assignment) params.append('faculty_assignment', filters.faculty_assignment);
  if (filters?.created_start) params.append('created_start', filters.created_start);
  if (filters?.created_end) params.append('created_end', filters.created_end);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));

  const url = params.toString()
    ? `${API_BASE}/api/admin/bookings?${params}`
    : `${API_BASE}/api/admin/bookings`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch bookings');
  }

  return result.data;
}

// SDA-297: one row per class. Returned by GET /bookings?view=classes.
export interface AdminBookingClassRow {
  class: {
    id: string;
    class_date: string;
    start_time: string | null;
    status: string;
    total_cents: number;
    faculty: { id: string; first_name: string; last_name: string } | null;
    faculty_assignment_status: string | null;
    tour_stop: {
      show: { name: string } | null;
      city: { name: string; state: string } | null;
    } | null;
  };
  booking: {
    id: string;
    booking_number: string | null;
    status: string;
    total_cents: number;
    balance_status: string | null;
    created_at: string;
    studio: {
      id: string;
      name: string;
      primary_contact: { first_name: string; last_name: string; email: string } | null;
    } | null;
    class_index: number;
    class_count: number;
  };
}

export async function getAdminBookingClassRows(
  filters?: BookingFilters,
): Promise<{ rows: AdminBookingClassRow[]; total: number; search_truncated?: boolean }> {
  const params = new URLSearchParams();
  params.append('view', 'classes');
  if (filters?.status) params.append('status', filters.status);
  if (filters?.show_id) params.append('show_id', filters.show_id);
  if (filters?.studio_id) params.append('studio_id', filters.studio_id);
  if (filters?.tour_stop_id) params.append('tour_stop_id', filters.tour_stop_id);
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  if (filters?.faculty_assignment) params.append('faculty_assignment', filters.faculty_assignment);
  if (filters?.created_start) params.append('created_start', filters.created_start);
  if (filters?.created_end) params.append('created_end', filters.created_end);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));

  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings?${params}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch bookings');
  }

  return result.data;
}

export async function getAdminBooking(bookingId: string): Promise<AdminBooking> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch booking');
  }

  return result.data;
}

export async function createManualBooking(input: CreateManualBookingInput): Promise<AdminBooking> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create booking');
  }

  return result.data;
}

export async function updateAdminBooking(bookingId: string, input: UpdateBookingInput): Promise<AdminBooking> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update booking');
  }

  return result.data;
}

export async function updateAdminClass(classId: string, input: UpdateClassInput): Promise<AdminClass> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/classes/${classId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update class');
  }

  return result.data;
}

export async function refundBooking(bookingId: string, input: RefundInput): Promise<RefundResult> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/refund`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to process refund');
  }

  return result.data;
}

export interface ReconcileBalanceInput {
  action: 'mark_paid' | 'waive';
  reference?: string;
  note?: string;
  /**
   * Optional partial amount (in cents) to record for a 'mark_paid' action.
   * When omitted, the server records the full remaining balance. When provided
   * and it only partially covers the remaining balance, the balance stays
   * 'pending' (still eligible for the auto-charge cron).
   */
  amount_cents?: number;
}

export interface ReconcileBalanceResult {
  balance_status: 'paid' | 'waived' | 'pending';
  amount_cents: number;
}

/**
 * Reconcile a booking's outstanding balance outside Stripe — either record an
 * external (e.g. QuickBooks) payment for the full remaining balance, or waive
 * it. Both stop the auto-charge cron. The amount is always server-computed.
 */
export async function reconcileBookingBalance(
  bookingId: string,
  input: ReconcileBalanceInput
): Promise<ReconcileBalanceResult> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/balance/reconcile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to reconcile balance');
  }

  return result.data;
}

/**
 * Email the studio's primary contact a login-gated link to pay the remaining
 * deposit online (for manual/offline bookings that still owe a deposit).
 * Paying via the link saves their card and arms the balance auto-charge.
 */
export async function sendDepositLink(bookingId: string): Promise<{ sent: boolean; email: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/send-deposit-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send deposit payment link');
  }

  return result.data;
}

/**
 * SDA-265: email the studio's primary contact a link to pay the outstanding
 * balance. Balance-side sibling of sendDepositLink.
 */
export async function sendBalanceLink(bookingId: string): Promise<{ sent: boolean; email: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/send-balance-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send balance payment link');
  }

  return result.data;
}

export async function cancelBooking(bookingId: string, input: CancelBookingInput): Promise<AdminBooking> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to cancel booking');
  }

  return result.data;
}

export async function addBookingNote(bookingId: string, content: string): Promise<AdminNote> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: content }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to add note');
  }

  return result.data;
}

export async function updateBookingNote(bookingId: string, noteId: string, content: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/notes/${noteId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: content }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update note');
  }
}

export async function deleteBookingNote(bookingId: string, noteId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/notes/${noteId}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete note');
  }
}

export async function sendMarketingMaterialsReminder(bookingId: string): Promise<{ reminder_sent_at: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/bookings/${bookingId}/marketing-materials-reminder`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send reminder');
  }

  return result.data;
}

// =============================================================================
// API Functions - Inquiries
// =============================================================================

export interface InquiryStats {
  total: number;
  pending: number;
  in_review: number;
  overdue: number;
  sla_breached: number;
  /** Average hours to first response (all-time); null until one exists */
  avgResponseTime?: number | null;
  respondedCount?: number;
}

export async function getAdminInquiries(filters?: InquiryFilters): Promise<{ inquiries: AdminInquiry[]; total: number; stats?: InquiryStats }> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.show_id) params.append('show_id', filters.show_id);
  if (filters?.city_id) params.append('city_id', filters.city_id);
  if (filters?.inquiry_type) params.append('inquiry_type', filters.inquiry_type);
  if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters?.sla_status) params.append('sla_status', filters.sla_status);
  if (filters?.source) params.append('source', filters.source);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('per_page', String(filters.limit));

  const url = params.toString()
    ? `${API_BASE}/api/admin/inquiries?${params}`
    : `${API_BASE}/api/admin/inquiries`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch inquiries');
  }

  // Backend returns { data: [...], sla_summary: {...}, pagination: {...} }
  // Map to the shape the frontend expects. Stats are global (whole-inbox);
  // pagination.total is the filtered count driving the table pager.
  const slaSummary = result.sla_summary || {};
  return {
    inquiries: result.data || [],
    total: result.pagination?.total || 0,
    stats: {
      total: slaSummary.total ?? result.pagination?.total ?? 0,
      pending: slaSummary.total_pending || 0,
      in_review: 0,
      overdue: slaSummary.breached || 0,
      sla_breached: slaSummary.breached || 0,
      avgResponseTime: slaSummary.avg_response_hours ?? null,
      respondedCount: slaSummary.responded_count || 0,
    },
  };
}

export async function getAdminInquiry(inquiryId: string): Promise<AdminInquiry> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/inquiries/${inquiryId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch inquiry');
  }

  return result.data;
}

export async function updateAdminInquiry(inquiryId: string, input: UpdateInquiryInput): Promise<AdminInquiry> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/inquiries/${inquiryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update inquiry');
  }

  return result.data;
}

export async function respondToInquiry(inquiryId: string, input: RespondToInquiryInput): Promise<AdminInquiry> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/inquiries/${inquiryId}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send response');
  }

  return result.data;
}

export async function convertInquiryToBooking(inquiryId: string, input: ConvertInquiryInput): Promise<AdminBooking> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/inquiries/${inquiryId}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to convert inquiry');
  }

  return result.data;
}

// =============================================================================
// API Functions - Faculty
// =============================================================================

/**
 * Transform raw faculty data to include computed/aliased properties for page compatibility
 */
function transformFacultyData(faculty: AdminFaculty): AdminFaculty {
  // Handle both nested users data and direct faculty columns
  const userData = faculty.users as { first_name?: string; last_name?: string; email?: string } | null;
  const firstName = faculty.first_name || userData?.first_name || '';
  const lastName = faculty.last_name || userData?.last_name || '';
  const email = faculty.email || userData?.email || '';

  return {
    ...faculty,
    // Computed properties
    first_name: firstName,
    last_name: lastName,
    email: email,
    name: `${firstName} ${lastName}`.trim(),
    // Backend returns 'shows' (plural) nested in faculty_shows; carry the
    // certification's active flag through as `cert_active` (SDA-118).
    shows: faculty.faculty_shows
      ?.map(fs => {
        const show = (fs as { shows?: Show }).shows;
        if (!show) return undefined;
        return { ...show, cert_active: (fs as { is_active?: boolean }).is_active ?? true };
      })
      .filter((s): s is Show & { cert_active: boolean } => !!s) || [],
    pay_rate_cents: faculty.default_rate_cents,
    upcoming_assignments: faculty.assignment_count || 0,
    stripe_connect_id: faculty.stripe_account_id,
    stripe_connect_status: faculty.stripe_account_status === 'enabled' || faculty.stripe_connected
      ? 'enabled'
      : faculty.stripe_account_status === 'restricted'
      ? 'restricted'
      : (faculty.stripe_account_id ? 'pending' : null),
  };
}

export async function getAdminFaculty(filters?: FacultyFilters): Promise<{ faculty: AdminFaculty[]; total: number; stats?: FacultyStats }> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.assignable) params.append('assignable', 'true');
  if (filters?.show_id) params.append('show_id', filters.show_id);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.exclude_date) params.append('exclude_date', filters.exclude_date);
  if (filters?.exclude_time) params.append('exclude_time', filters.exclude_time);
  if (filters?.exclude_class_id) params.append('exclude_class_id', filters.exclude_class_id);
  if (filters?.exclude_faculty_id) params.append('exclude_faculty_id', filters.exclude_faculty_id);

  const url = params.toString()
    ? `${API_BASE}/api/admin/faculty?${params}`
    : `${API_BASE}/api/admin/faculty`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch faculty');
  }

  // Transform each faculty member to include computed properties
  const faculty = (result.data.faculty || []).map(transformFacultyData);
  return {
    faculty,
    total: result.data.total || 0,
    stats: result.data.stats,
  };
}

export async function getAdminFacultyMember(facultyId: string): Promise<AdminFaculty> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch faculty member');
  }

  return transformFacultyData(result.data);
}

/**
 * Thrown by createFaculty when the email already belongs to an existing
 * faculty record. `hasAccount` (derived from the row's user_id) tells the
 * form whether to point the admin at "View profile" (account exists) or
 * "Resend Invitation" (orphaned row, no linked account yet).
 */
export class DuplicateFacultyError extends Error {
  facultyId: string;
  status: string;
  hasAccount: boolean;
  constructor(message: string, facultyId: string, status: string, hasAccount: boolean) {
    super(message);
    this.name = 'DuplicateFacultyError';
    this.facultyId = facultyId;
    this.status = status;
    this.hasAccount = hasAccount;
  }
}

export async function createFaculty(input: CreateFacultyInput): Promise<AdminFaculty> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    const message = result.error?.message || 'Failed to create faculty';
    if (result.error?.code === 'DUPLICATE') {
      const details = result.error?.details as { faculty_id?: string; status?: string; has_account?: boolean } | undefined;
      if (details?.faculty_id) {
        throw new DuplicateFacultyError(message, details.faculty_id, details.status ?? '', details.has_account ?? true);
      }
    }
    throw new Error(message);
  }

  return result.data;
}

export async function updateAdminFaculty(facultyId: string, input: UpdateFacultyInput): Promise<AdminFaculty> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update faculty');
  }

  return result.data;
}

export async function approveFaculty(facultyId: string): Promise<AdminFaculty> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/approve`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to approve faculty');
  }

  return result.data;
}

export async function overrideActivateFaculty(
  facultyId: string,
): Promise<AdminFaculty> {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/faculty/${facultyId}/override-activate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to override-activate faculty');
  }

  return result.data;
}

export async function uploadFacultyHeadshot(
  facultyId: string,
  file: File,
): Promise<{ headshotUrl: string }> {
  const formData = new FormData();
  formData.append('headshot', file);

  // Do NOT set Content-Type — browser must set the multipart boundary.
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/faculty/${facultyId}/headshot`,
    {
      method: 'POST',
      body: formData,
    },
  );
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to upload headshot');
  }

  return result.data;
}

export async function removeFacultyHeadshot(
  facultyId: string,
): Promise<{ headshotUrl: null }> {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/faculty/${facultyId}/headshot`,
    { method: 'DELETE' },
  );
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to remove headshot');
  }

  return result.data;
}

export async function assignFacultyToClass(facultyId: string, classId: string): Promise<AdminClass> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ class_id: classId }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to assign faculty');
  }

  return result.data;
}

// SDA-277: remove the assigned faculty so the class returns to Unassigned
// (e.g. they backed out after accepting). No emails fire on unassign.
export async function unassignFacultyFromClass(classId: string): Promise<{ unassigned: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/classes/${classId}/faculty`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to unassign faculty');
  }

  return result.data;
}

// SDA-276: legacy (QBO-migrated) bookings sometimes have faculty already paid
// outside the system. This records that — no money moves, and it locks the
// class out of Mark Complete's payout scheduling and the payout cron, both of
// which gate on faculty_paid = false.
export interface MarkFacultyPaidExternalInput {
  reference?: string;
  note?: string;
}

export async function markFacultyPaidExternal(
  classId: string,
  input: MarkFacultyPaidExternalInput
): Promise<{ faculty_paid: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/classes/${classId}/faculty-paid-external`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to mark faculty paid');
  }

  return result.data;
}

// SDA-296: Mark ONE class complete (multi-class bookings). Queues that class's
// faculty payout; the booking completes itself after its last class only when
// its balance is settled.
export interface CompleteClassResult {
  class_id: string;
  payout: 'created' | 'exists' | 'none';
  skipped_payouts: Array<{
    classId: string;
    classDate: string | null;
    facultyId: string | null;
    facultyName: string;
    amountCents: number;
    reason: string;
  }>;
  booking_completed: boolean;
  booking_open_reason: 'balance_open' | null;
}

export async function completeClass(classId: string): Promise<CompleteClassResult> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/classes/${classId}/complete`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to mark class complete');
  }

  return result.data;
}

export interface AvailabilityBlock {
  id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  is_recurring?: boolean;
}

export interface AvailabilityAssignment {
  id: string;
  class_date: string;
  time_slot: string | null;
  status: string;
  assignment_status: string | null;
  tour_stops: { shows: { name: string } | null; cities: { name: string; state: string } | null } | null;
  bookings: { studios: { name: string } | null } | null;
}

export async function getFacultyAvailability(facultyId: string, startDate: string, endDate: string): Promise<{
  availability_blocks: AvailabilityBlock[];
  assignments: AvailabilityAssignment[];
}> {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/faculty/${facultyId}/availability?start_date=${startDate}&end_date=${endDate}`
  );
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch availability');
  }

  return result.data;
}

export async function addFacultyAvailabilityBlock(facultyId: string, input: { start_date: string; end_date: string; reason?: string }): Promise<AvailabilityBlock> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to add blocked dates');
  }

  return result.data;
}

export async function removeFacultyAvailabilityBlock(facultyId: string, blockId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/availability/${blockId}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to remove blocked dates');
  }
}

export async function addFacultyShowCertification(facultyId: string, showId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/shows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ show_id: showId }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to add show certification');
  }
}

export async function removeFacultyShowCertification(facultyId: string, showId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/shows/${showId}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to remove show certification');
  }
}

/**
 * Activate / deactivate a show certification (SDA-118). Inactive certifications
 * stay on record but exclude the faculty from assignment to that show.
 */
export async function setFacultyShowActive(facultyId: string, showId: string, isActive: boolean): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/shows/${showId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update show certification');
  }
}

export async function resendFacultyInvitation(facultyId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/resend-invitation`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to resend invitation');
  }
}

export async function sendFacultyStripeReminder(facultyId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/send-stripe-reminder`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send Stripe reminder');
  }
}

export async function getFacultyAssignments(facultyId: string): Promise<ClassAssignment[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/assignments`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch faculty assignments');
  }

  return result.data || [];
}

export async function updateFacultyStatus(
  facultyId: string,
  status: 'invited' | 'onboarding' | 'pending_approval' | 'active' | 'inactive'
): Promise<AdminFaculty> {
  return updateAdminFaculty(facultyId, { status });
}

export async function updateFacultyPayRate(facultyId: string, payRateCents: number): Promise<AdminFaculty> {
  return updateAdminFaculty(facultyId, { default_rate_cents: payRateCents });
}

// =============================================================================
// Utility Functions
// =============================================================================

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export function formatBookingStatus(status: string): string {
  const statusLabels: Record<string, string> = {
    pending: 'Pending',
    deposit_paid: 'Deposit Paid',
    confirmed: 'Confirmed',
    balance_paid: 'Balance Paid',
    completed: 'Completed',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
  };
  return statusLabels[status] || status;
}

export function getBookingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-warning-100 text-warning-700',
    deposit_paid: 'bg-primary-100 text-primary-700',
    confirmed: 'bg-success-100 text-success-700',
    balance_paid: 'bg-success-100 text-success-700',
    completed: 'bg-primary-100 text-primary-700',
    cancelled: 'bg-warm-100 text-warm-700',
    refunded: 'bg-danger-100 text-danger-700',
  };
  return colors[status] || 'bg-warm-100 text-warm-700';
}

export function formatInquiryStatus(status: string): string {
  const statusLabels: Record<string, string> = {
    pending: 'Pending',
    in_review: 'In Review',
    responded: 'Responded',
    converted: 'Converted',
    closed: 'Closed',
  };
  return statusLabels[status] || status;
}

export function getInquiryStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-warning-100 text-warning-700',
    in_review: 'bg-primary-100 text-primary-700',
    responded: 'bg-success-100 text-success-700',
    converted: 'bg-success-100 text-success-700',
    closed: 'bg-warm-100 text-warm-700',
  };
  return colors[status] || 'bg-warm-100 text-warm-700';
}

export function getSlaStatusColor(inquiry: AdminInquiry): { color: string; label: string } {
  if (inquiry.sla_breached_at) {
    return { color: 'bg-danger-100 text-danger-700', label: 'Overdue (24h+)' };
  }

  if (!inquiry.response_due_at) {
    return { color: 'bg-warm-100 text-warm-600', label: 'N/A' };
  }

  const dueAt = new Date(inquiry.response_due_at);
  const now = new Date();
  const hoursUntilDue = (dueAt.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntilDue < 0) {
    return { color: 'bg-danger-100 text-danger-700', label: 'Overdue' };
  }

  if (hoursUntilDue < 24) {
    return { color: 'bg-warning-100 text-warning-700', label: 'Due Soon' };
  }

  return { color: 'bg-success-100 text-success-700', label: 'On Track' };
}

export function formatFacultyStatus(status: string): string {
  const statusLabels: Record<string, string> = {
    invited: 'Invited',
    onboarding: 'Onboarding',
    pending_approval: 'Pending Approval',
    active: 'Active',
    inactive: 'Inactive',
  };
  return statusLabels[status] || status;
}

export function getFacultyStatusColor(status: string): string {
  const colors: Record<string, string> = {
    invited: 'bg-primary-100 text-primary-700',
    onboarding: 'bg-warning-100 text-warning-700',
    pending_approval: 'bg-warning-100 text-warning-700',
    active: 'bg-success-100 text-success-700',
    inactive: 'bg-warm-100 text-warm-700',
  };
  return colors[status] || 'bg-warm-100 text-warm-700';
}

// =============================================================================
// Types - Studios (for admin booking form)
// =============================================================================

export interface Studio {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  latitude: number | null;
  longitude: number | null;
  primary_contact?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
  contacts?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    is_primary: boolean;
  }[];
  created_at: string;
}

export interface AdminStudio {
  id: string;
  name: string;
  slug: string;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip_code: string;
  phone: string | null;
  website: string | null;
  student_count: number | null;
  year_established: number | null;
  is_active: boolean;
  /**
   * True until the studio activates an account. SDA-281: this — not is_active — is
   * what the Active/Inactive badge reads. is_active defaults true and nothing ever
   * sets it false.
   */
  is_dormant: boolean;
  is_verified: boolean;
  total_bookings: number;
  total_spent_cents: number;
  stripe_customer_id: string | null;
  created_at: string;
  primary_contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  } | null;
  contact_count: number;
  cities: { id: string; name: string; state: string; match_type?: 'address' | 'proximity' }[];
}

export interface AdminStudioDetail extends AdminStudio {
  contacts: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    title: string | null;
    is_primary: boolean;
    receives_marketing_emails: boolean;
    receives_booking_emails: boolean;
    receives_reminder_emails: boolean;
    /** Set when the bounce scan auto-unsubscribed this contact (hard bounce). */
    bounce_unsubscribe?: { at: string; reason: string } | null;
  }[];
  bookings?: {
    id: string;
    booking_number: string;
    status: string;
    total_cents: number;
    deposit_paid_cents: number;
    balance_due_cents: number;
    balance_paid_cents: number;
    balance_status: string | null;
    balance_due_date: string | null;
    created_at: string;
    class_date: string | null;
    show_name: string | null;
    city: string | null;
    state: string | null;
  }[];
}

export interface AdminStudiosResponse {
  studios: AdminStudio[];
  total: number;
  /** SDA-264: set when the contact-name pre-query hit its cap, so results may be partial. */
  search_truncated?: boolean;
  stats: {
    total_studios: number;
    active_studios: number;
    verified_studios: number;
    with_bookings: number;
  };
}

// =============================================================================
// Types - Admin Users (for inquiry assignment)
// =============================================================================

export interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  // Computed property for display
  name?: string;
  email: string;
  role: 'admin' | 'staff';
  is_active: boolean;
  created_at: string;
}

// =============================================================================
// Types - Email Templates (for inquiry responses)
// =============================================================================

export interface EmailTemplate {
  id: string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  subject: string;
  content: string;
  // Alias for content (some pages use 'body')
  body?: string;
  preview_text?: string | null;
  html_content?: string;
  plain_text_content?: string | null;
  template_type: 'inquiry_response' | 'booking_confirmation' | 'reminder' | 'general';
  template_kind?: 'transactional' | 'campaign';
  editable?: boolean;
  is_system_template: boolean;
  last_edited_at?: string | null;
  last_edited_by?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

// =============================================================================
// API Functions - Studios (for admin)
// =============================================================================

export async function getStudios(filters?: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ studios: Studio[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.page) params.append('page', String(filters.page));
  if (filters?.limit) params.append('limit', String(filters.limit));

  const url = params.toString()
    ? `${API_BASE}/api/admin/studios?${params}`
    : `${API_BASE}/api/admin/studios`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch studios');
  }

  return result.data;
}

export async function getStudio(studioId: string): Promise<Studio> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch studio');
  }

  return result.data;
}

/**
 * SDA-292: the card the studio's next payment will be charged to (read-only).
 */
export async function getStudioSavedCard(studioId: string): Promise<SavedCard | null> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}/card`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch saved card');
  }

  return result.data.card;
}

export async function getAdminStudios(params?: {
  search?: string;
  page?: number;
  limit?: number;
  is_active?: string;
  state?: string;
  has_bookings?: string;
  sort_by?: string;
  sort_order?: string;
}): Promise<AdminStudiosResponse> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.append('search', params.search);
  if (params?.page) searchParams.append('page', String(params.page));
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.is_active) searchParams.append('is_active', params.is_active);
  if (params?.state) searchParams.append('state', params.state);
  if (params?.has_bookings) searchParams.append('has_bookings', params.has_bookings);
  if (params?.sort_by) searchParams.append('sort_by', params.sort_by);
  if (params?.sort_order) searchParams.append('sort_order', params.sort_order);

  const url = searchParams.toString()
    ? `${API_BASE}/api/admin/studios?${searchParams}`
    : `${API_BASE}/api/admin/studios`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch studios');
  }

  return result.data;
}

export async function getAdminStudio(studioId: string): Promise<AdminStudioDetail> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch studio');
  }

  return result.data;
}

/** SDA-299: one tracked website visit for a studio. */
export interface StudioPageView {
  id: string;
  page_type: string;
  path: string;
  created_at: string;
  show_name: string | null;
  city_name: string | null;
  city_state: string | null;
}

/** SDA-299: the studio's latest 200 tracked website visits, newest first. */
export async function getAdminStudioPageViews(studioId: string): Promise<StudioPageView[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}/page-views`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch website visits');
  }

  return result.data.visits;
}

// =============================================================================
// API Functions - Admin Users
// =============================================================================

export async function getAdminUsers(): Promise<AdminUser[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/users`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch admin users');
  }

  return result.data;
}

// =============================================================================
// API Functions - Email Templates
// =============================================================================

export async function getEmailTemplates(
  opts?: string | { type?: string; kind?: 'transactional' | 'campaign' },
): Promise<EmailTemplate[]> {
  // Back-compat: legacy callers pass a string templateType. New callers pass { kind, type }.
  const normalized = typeof opts === 'string' ? { type: opts } : (opts ?? {});
  const params = new URLSearchParams();
  if (normalized.type) params.append('type', normalized.type);
  if (normalized.kind) params.append('kind', normalized.kind);

  const url = params.toString()
    ? `${API_BASE}/api/admin/email-templates?${params}`
    : `${API_BASE}/api/admin/email-templates`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch email templates');
  }

  return result.data;
}

export async function updateEmailTemplate(
  id: string,
  payload: Partial<{ subject: string; display_name: string | null; preview_text: string | null; html_content: string; plain_text_content: string | null; description: string | null }>,
): Promise<EmailTemplate> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update email template');
  }
  return result.data;
}

export async function createEmailTemplate(payload: {
  display_name: string;
  description?: string;
  subject: string;
  preview_text?: string;
  html_content: string;
  plain_text_content?: string;
}): Promise<EmailTemplate> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create email template');
  }
  return result.data;
}

export async function deleteEmailTemplate(id: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-templates/${id}`, {
    method: 'DELETE',
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete email template');
  }
}

/** Raw editor content for the SDA-195 template preview / test-send endpoints.
 *  Sent as-is (no campaign) — the server substitutes representative sample
 *  values, so the preview and the test email render identically. */
export interface TemplatePreviewContent {
  subject: string;
  html_content: string;
  preview_text?: string;
  plain_text_content?: string;
  /** SDA-195 follow-up: optionally render against a real show's live data
   *  (name/city/dates/pricing). Omitted → generic sample values. */
  show_id?: string;
}

/**
 * SDA-195: render the CURRENT (possibly unsaved) template editor content with
 * sample values for the Templates-tab preview. Render only — never sends.
 */
export async function renderTemplatePreview(
  content: TemplatePreviewContent,
): Promise<{ subject: string; html: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-templates/render-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(content),
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to render template preview');
  return result.data;
}

/**
 * SDA-195: send one test copy of the CURRENT template editor content to the
 * logged-in admin's own inbox (subject prefixed "[TEST] "). Targets ONLY the
 * admin — no campaign, no recipients, no DB writes.
 */
export async function sendTestTemplateEmail(
  content: TemplatePreviewContent,
): Promise<{ sent_to: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-templates/send-test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(content),
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to send test email');
  return result.data;
}

// =============================================================================
// API Functions - Inquiry Helpers
// =============================================================================

export async function updateInquiryStatus(inquiryId: string, status: string): Promise<AdminInquiry> {
  return updateAdminInquiry(inquiryId, { status });
}

export async function assignInquiry(inquiryId: string, userId: string | null): Promise<AdminInquiry> {
  return updateAdminInquiry(inquiryId, { assigned_to: userId });
}

// =============================================================================
// Type Aliases (for page compatibility)
// =============================================================================

// =============================================================================
// API Functions - Campaigns
// =============================================================================

export interface CampaignAttributedMetrics {
  signups: number;
  bookings: number;
  inquiries: number;
  waitlist: number;
  total: number;
}

export interface CampaignMetrics {
  total_sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  failed: number;
  unsubscribed: number;
  spam_reported: number;
  open_rate: string;
  click_rate: string;
  bounce_rate: string;
  /** SDA-267: sign-ups stamped with this campaign's id. */
  attributed?: CampaignAttributedMetrics;
}

/** SDA-267: per-campaign email stats merged onto the campaigns list. */
export interface CampaignEmailStats {
  total: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
}

export interface AdminCampaign {
  id: string;
  name: string;
  subject: string;
  preview_text: string | null;
  html_content: string;
  plain_text_content: string | null;
  campaign_type: string;
  status: string;
  scheduled_for: string | null;
  scheduled_timezone: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_recipients: number;
  sent_count: number;
  target_show_id: string | null;
  target_city_id: string | null;
  target_show_ids: string[] | null;
  target_city_ids: string[] | null;
  exclude_booked: boolean;
  include_interested?: boolean;
  created_at: string;
  created_by?: { first_name: string; last_name: string; email?: string } | null;
  tour_stop_id: string | null;
  parent_campaign_id: string | null;
  sequence_number: number;
  auto_generated: boolean;
  requires_approval: boolean;
  approved_by: string | null;
  approved_at: string | null;
  auto_approve_reminders: boolean;
  resume_after: string | null;
  metrics?: CampaignMetrics;
  quota?: { daily_limit: number; quota_used: number; quota_remaining: number };
}

export interface EmailSendRecord {
  id: string;
  contact_id: string | null;
  recipient_email: string;
  recipient_name: string | null;
  status: string;
  sent_at: string | null;
  failure_reason: string | null;
  contacts?: { first_name: string; last_name: string; studios?: { name: string } } | null;
}

export interface CreateCampaignInput {
  name: string;
  subject: string;
  preview_text?: string;
  html_content: string;
  plain_text_content?: string;
  campaign_type: string;
  scheduled_for?: string;
  scheduled_timezone?: string;
  show_ids?: string[];
  city_ids?: string[];
  exclude_booked?: boolean;
  include_interested?: boolean;
}

export interface UpdateCampaignInput {
  name?: string;
  subject?: string;
  preview_text?: string;
  html_content?: string;
  plain_text_content?: string;
  scheduled_for?: string | null;
  scheduled_timezone?: string | null;
  show_ids?: string[];
  city_ids?: string[];
  exclude_booked?: boolean;
  include_interested?: boolean;
  auto_approve_reminders?: boolean;
}

export interface CampaignChainTourStop {
  tour_stop_id: string;
  show_id: string;
  show_name: string;
  city: string;
  state: string;
  theater: string;
  start_date: string;
  end_date: string;
  campaigns: QueuedCampaign[];
}

export interface TargetCity {
  id: string;
  name: string;
  state: string;
}

export interface QueuedCampaign extends AdminCampaign {
  // null while the audience side-load is in flight; populated by /queue/audience.
  estimated_audience: number | null;
  cadence_warning: string | null;
  target_cities: TargetCity[];
}

export type CampaignQueueAudienceMap = Record<
  string,
  { estimated_audience: number }
>;

export interface ScheduledSend {
  id: string;
  name: string;
  campaign_type: string;
  status: string;
  sequence_number: number;
  scheduled_for: string;
  scheduled_timezone: string | null;
  show_name: string | null;
  city: string | null;
  estimated_audience: number | null;
  tour_stop_start: string | null;
  resume_after: string | null;
  sent_count: number;
  /** SDA-245: estimated first/last send date (YYYY-MM-DD, ET) for quota-parked
   *  rows, simulated from the current daily limit; null for upcoming rows or
   *  when the audience is unknown. */
  est_start_at: string | null;
  est_complete_at: string | null;
}

export interface CampaignRecipient {
  studio_name: string;
  contact_name: string;
  email: string;
  city: string;
  state: string;
}

export interface CampaignRecipientsResponse {
  recipients: CampaignRecipient[];
  total: number;
  suppressed: {
    booked_count: number;
    booked_studios: string[];
  };
}

export interface CampaignQueueResponse {
  chains: CampaignChainTourStop[];
  total_pending: number;
  total_chains?: number;
}

export interface ScheduledSendsResponse {
  scheduled: ScheduledSend[];
  total: number;
}

export async function getCampaign(campaignId: string): Promise<AdminCampaign> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch campaign');
  }

  return result.data;
}

export async function createCampaign(input: CreateCampaignInput): Promise<AdminCampaign> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create campaign');
  }

  return result.data;
}

export async function updateCampaign(campaignId: string, input: UpdateCampaignInput): Promise<AdminCampaign> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update campaign');
  }

  return result.data;
}

// ---------------------------------------------------------------------------
// Campaign Email Sends
// ---------------------------------------------------------------------------

export async function getCampaignSends(
  campaignId: string,
  options?: { status?: string; page?: number; perPage?: number }
): Promise<{ data: EmailSendRecord[]; pagination: { total: number; page: number; total_pages: number } }> {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  params.append('page', String(options?.page || 1));
  params.append('per_page', String(options?.perPage || 50));

  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/sends?${params}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch sends');
  }

  return { data: result.data, pagination: result.pagination };
}

// ---------------------------------------------------------------------------
// Admin Contact Management
// ---------------------------------------------------------------------------

export interface CreateAdminContactInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  title?: string | null;
  is_primary?: boolean;
  receives_marketing_emails?: boolean;
  receives_booking_emails?: boolean;
  receives_reminder_emails?: boolean;
}

export type AdminContact = AdminStudioDetail['contacts'][number];

export async function createAdminContact(studioId: string, input: CreateAdminContactInput): Promise<AdminContact> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json() as { success: boolean; data?: { contact: AdminContact }; error?: { message: string } };

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create contact');
  }

  return result.data!.contact;
}

export interface CreateAdminStudioInput {
  name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code: string;
  phone?: string | null;
  website?: string | null;
  city_id?: string | null;
  /** Google Places locality place_id — lets the backend link a canonical city. */
  google_place_id?: string;
  primary_contact?: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string | null;
  };
  send_invite_email?: boolean;
}

export interface CreateAdminStudioResult {
  studio: { id: string; name: string; slug: string };
  email_sent: boolean;
}

export async function createAdminStudio(
  input: CreateAdminStudioInput,
): Promise<CreateAdminStudioResult> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json() as {
    success: boolean;
    data?: { studio: { id: string; name: string; slug: string }; email_sent: boolean };
    error?: { message: string };
  };

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create studio');
  }

  return { studio: result.data!.studio, email_sent: result.data!.email_sent };
}

export async function deleteAdminStudio(studioId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}`, {
    method: 'DELETE',
  });
  const result = await response.json() as { success: boolean; error?: { message: string } };

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete studio');
  }
}

/**
 * Send (or resend) the activation email to a dormant studio's primary contact,
 * inviting them to claim their account. Backend resolves the recipient.
 */
export async function sendStudioActivationEmail(
  studioId: string
): Promise<{ email_sent: boolean; recipient: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}/send-activation`, {
    method: 'POST',
  });
  const result = await response.json() as { success: boolean; data?: { email_sent: boolean; recipient: string }; error?: { message: string } };

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to send activation email');
  }

  return result.data;
}

export interface UpdateAdminStudioInput {
  name?: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string;
  phone?: string | null;
  website?: string | null;
  /** Google Places locality place_id — lets the backend re-link a canonical city. */
  google_place_id?: string;
}

export async function updateAdminStudio(studioId: string, input: UpdateAdminStudioInput): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/studios/${studioId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json() as { success: boolean; error?: { message: string } };

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update studio');
  }
}

export interface UpdateAdminContactInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  title?: string | null;
  receives_marketing_emails?: boolean;
  receives_booking_emails?: boolean;
  receives_reminder_emails?: boolean;
  is_active?: boolean;
  is_primary?: boolean;
}

export async function updateAdminContact(contactId: string, input: UpdateAdminContactInput): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/contacts/${contactId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update contact');
  }
}

export async function batchUnsubscribeContacts(contactIds: string[]): Promise<{ updated: number }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/contacts/batch-unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact_ids: contactIds }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to unsubscribe contacts');
  }

  return result.data;
}

// ---------------------------------------------------------------------------
// Campaign Sending
// ---------------------------------------------------------------------------

export async function sendCampaign(campaignId: string, sendImmediately: boolean = true): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ send_immediately: sendImmediately }),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to send campaign');
  }
}

/** A KNOWN campaign token that resolved blank and was substituted with a friendly
 *  fallback (SDA-192). `rendered_as` is the human-readable value shown ('(blank)'
 *  for tokens whose fallback is an empty string). */
export interface CampaignFallback {
  token: string;
  rendered_as: string;
}

/**
 * SDA-192: send a single test copy of the campaign to the logged-in admin's own
 * inbox (subject prefixed "[TEST] "). Renders identically to a real send so the
 * admin can verify look/feel before approving. Returns which KNOWN tokens fell
 * back to a friendly value so the UI can flag them. Targets ONLY the admin.
 */
export async function sendTestCampaignEmail(
  campaignId: string,
): Promise<{ sent_to: string; fallbacks_applied: CampaignFallback[] }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/send-test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to send test email');
  return result.data;
}

export async function stopCampaign(campaignId: string): Promise<{ stopped: boolean; sent_count: number }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/stop`, {
    method: 'POST',
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to stop campaign');
  return result.data;
}

/** Cancel a scheduled campaign so the cron never sends it (status → cancelled). */
export async function cancelCampaign(campaignId: string): Promise<{ cancelled: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/cancel`, {
    method: 'POST',
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to cancel campaign');
  return result.data;
}

export async function deleteCampaign(campaignId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete campaign');
  }
}

// =============================================================================
// API Functions - Interest Subscriptions
// =============================================================================

export interface InterestSubscription {
  id: string;
  contact_email: string;
  contact_name: string;
  show_id: string;
  show_name?: string;
  preferred_cities: string[];
  is_active: boolean;
  created_at: string;
}

export async function getInterestSubscriptions(params?: { show_id?: string; page?: number; per_page?: number }): Promise<{ subscriptions: InterestSubscription[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params?.show_id) searchParams.set('show_id', params.show_id);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.per_page) searchParams.set('per_page', String(params.per_page));

  const response = await authenticatedFetch(`${API_BASE}/api/admin/interest-subscriptions?${searchParams}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch interest subscriptions');
  }

  return result.data;
}

export async function deleteInterestSubscription(id: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/interest-subscriptions/${id}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete interest subscription');
  }
}

// =============================================================================
// API Functions - Campaign Templates
// =============================================================================

export interface CampaignTemplate {
  id: string;
  name: string;
  subject: string;
  html_content: string;
  description: string;
  campaign_type: string;
  is_system_template?: boolean;
}

export async function getCampaignTemplates(): Promise<{ templates: CampaignTemplate[] }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/templates`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch campaign templates');
  }

  return { templates: result.data as CampaignTemplate[] };
}

export async function resolveCampaignTemplate(campaignId: string): Promise<{ resolved_html: string; variables_used: string[]; variables_available?: Record<string, string>; fallbacks_applied?: CampaignFallback[] }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/resolve-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to resolve campaign template');
  }

  return result.data;
}

/**
 * SDA-192: render a campaign EXACTLY as a recipient would receive it (variables
 * resolved with friendly fallbacks + unsubscribe footer), for the queue/editor
 * preview. Renders only — never sends.
 */
export async function renderCampaignPreview(
  campaignId: string,
): Promise<{ subject: string; html: string; fallbacks_applied: CampaignFallback[] }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/render-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to render preview');
  return result.data;
}

export interface CampaignAudience {
  estimated_audience: number;
  sample_recipients: Array<{ name: string; email: string; studio?: string; city?: string }>;
  audience_cities?: Array<{ city: string; state: string; count: number; is_target: boolean }>;
}

export async function getCampaignAudience(
  campaignId: string,
  showAll = false,
  targeting?: { show_ids?: string[]; city_ids?: string[]; exclude_booked?: boolean; include_interested?: boolean },
): Promise<CampaignAudience> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/preview${showAll ? '?show_all=true' : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(targeting ?? {}),
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to get audience');
  return result.data as CampaignAudience;
}

// =============================================================================
// API Functions - Campaign Quota
// =============================================================================

export interface CampaignQuota {
  daily_limit: number;
  quota_used: number;
  quota_remaining: number;
  awaiting_send_count: number;
}

export async function getCampaignQuota(): Promise<CampaignQuota> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/quota`);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to get quota');
  return result.data;
}

// =============================================================================
// API Functions - Campaign Queue & Chains
// =============================================================================

export async function getCampaignQueue(): Promise<CampaignQueueResponse> {
  // /queue is fast — returns chain shells with estimated_audience: null.
  // Call getCampaignQueueAudience() afterward to hydrate audience values.
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/queue`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch campaign queue');
  }

  return result.data;
}

export async function getCampaignQueueAudience(): Promise<CampaignQueueAudienceMap> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/queue/audience`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch campaign queue audience');
  }

  return (result.data?.audience ?? {}) as CampaignQueueAudienceMap;
}

export async function getScheduledSends(): Promise<ScheduledSendsResponse> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/scheduled`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch scheduled sends');
  }

  return result.data;
}

export async function approveCampaign(
  campaignId: string,
  opts?: {
    scheduled_for?: string;
    scheduled_timezone?: string;
    auto_approve_reminders?: boolean;
  }
): Promise<{ approved: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts || {}),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to approve campaign');
  }

  return result.data;
}

export async function approveChain(tourStopId: string): Promise<{ approved: boolean; count: number }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/chain/${tourStopId}/approve-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to approve chain');
  }

  return result.data;
}

export async function dismissCampaign(campaignId: string): Promise<{ dismissed: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to dismiss campaign');
  }

  return result.data;
}

export async function dismissChain(tourStopId: string): Promise<{ dismissed: boolean; count: number }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/chain/${tourStopId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to dismiss chain');
  }

  return result.data;
}

// SDA-190: unschedule a whole chain back to draft (scheduled → draft, clears scheduled_for).
export async function unscheduleChain(tourStopId: string): Promise<{ unscheduled: boolean; count: number }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/chain/${tourStopId}/unschedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to unschedule chain');
  }

  return result.data;
}

export async function addReminderToChain(
  tourStopId: string,
  data: {
    scheduled_for: string;
    scheduled_timezone?: string;
    subject?: string;
    html_content?: string;
  }
): Promise<AdminCampaign> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/chain/${tourStopId}/add-reminder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to add reminder to chain');
  }

  return result.data;
}

export async function getCampaignRecipients(campaignId: string): Promise<CampaignRecipientsResponse> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/campaigns/${campaignId}/recipients`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch campaign recipients');
  }

  return result.data;
}

// =============================================================================
// API Functions - Coupons
// =============================================================================

export async function getCoupons(params?: { is_active?: boolean }): Promise<Coupon[]> {
  const searchParams = new URLSearchParams();
  if (params?.is_active !== undefined) searchParams.append('is_active', String(params.is_active));

  const url = searchParams.toString()
    ? `${API_BASE}/api/admin/coupons?${searchParams}`
    : `${API_BASE}/api/admin/coupons`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch coupons');
  }

  return result.data;
}

export async function getCoupon(couponId: string): Promise<CouponDetail> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/coupons/${couponId}`);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch coupon');
  }

  return result.data;
}

export async function createCoupon(input: CreateCouponInput): Promise<Coupon> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/coupons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to create coupon');
  }

  return result.data;
}

export async function updateCoupon(couponId: string, input: UpdateCouponInput): Promise<Coupon> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/coupons/${couponId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to update coupon');
  }

  return result.data;
}

export async function deleteCoupon(couponId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/coupons/${couponId}`, {
    method: 'DELETE',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete coupon');
  }
}

export async function toggleCoupon(couponId: string): Promise<Coupon> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/coupons/${couponId}/toggle`, {
    method: 'PATCH',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to toggle coupon');
  }

  return result.data;
}

// =============================================================================
// Types - Faculty Payouts
// =============================================================================

export interface FacultyPayoutAdmin {
  id: string;
  faculty_id: string;
  faculty_name: string;
  /** null for one-off payments (reimbursement/bonus/other, SDA-221) */
  class_id: string | null;
  class_date: string | null;
  show_name: string | null;
  studio_name: string | null;
  booking_id: string | null;
  booking_number: string | null;
  amount_cents: number;
  status: string;
  scheduled_for: string | null;
  processed_at: string | null;
  stripe_transfer_id: string | null;
  failure_reason: string | null;
  source?: 'auto' | 'manual';
  payment_type: string;
  note: string | null;
  created_at: string;
  retry_count?: number;
}

// =============================================================================
// API Functions - Faculty Payouts
// =============================================================================

export interface FacultyPayoutsSummary {
  total_pending_cents: number;
  total_paid_cents: number;
  total_failed_count: number;
  total_failed_cents: number;
}

export async function getAdminFacultyPayouts(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  faculty_id?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<{ payouts: FacultyPayoutAdmin[]; total: number; summary: FacultyPayoutsSummary }> {
  const searchParams = new URLSearchParams();
  if (params?.page !== undefined) searchParams.append('page', String(params.page));
  if (params?.per_page !== undefined) searchParams.append('per_page', String(params.per_page));
  if (params?.status) searchParams.append('status', params.status);
  if (params?.faculty_id) searchParams.append('faculty_id', params.faculty_id);
  if (params?.search) searchParams.append('search', params.search);
  if (params?.sort_by) searchParams.append('sort_by', params.sort_by);
  if (params?.sort_order) searchParams.append('sort_order', params.sort_order);

  const url = searchParams.toString()
    ? `${API_BASE}/api/admin/faculty-payouts?${searchParams}`
    : `${API_BASE}/api/admin/faculty-payouts`;

  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to fetch faculty payouts');
  }

  return result.data;
}

/**
 * Thrown by createFacultyPayout on a 409 duplicate guard. `existingStatus`
 * (when the server provides it) lets the modal decide whether "Create Anyway"
 * is offered: only failed/cancelled duplicates may be overridden (SDA-266).
 */
export class DuplicatePayoutWarning extends Error {
  existingStatus: string | null;
  constructor(message: string, existingStatus: string | null) {
    super(message);
    this.name = 'DuplicatePayoutWarning';
    this.existingStatus = existingStatus;
  }
}

export async function createFacultyPayout(data: {
  class_id: string;
  amount_cents?: number;
  scheduled_for?: string;
  force_create?: boolean;
}): Promise<FacultyPayoutAdmin> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty-payouts/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!result.success) {
    const message = result.error?.message || 'Failed to create faculty payout';
    if (response.status === 409) {
      const details = result.error?.details as { existing_status?: string } | undefined;
      throw new DuplicatePayoutWarning(message, details?.existing_status ?? null);
    }
    throw new Error(message);
  }

  return result.data;
}

export async function processFacultyPayout(payoutId: string): Promise<FacultyPayoutAdmin> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty-payouts/${payoutId}/process`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to process faculty payout');
  }

  return result.data;
}

/**
 * SDA-266: retry a FAILED payout. The server resets the row to pending,
 * bumps retry_count (rotating the Stripe idempotency key past the cached
 * failure), and re-runs the transfer. A `processed: false` response means
 * the retry itself failed again — the row is back in `failed` with a fresh
 * reason (see `error`).
 */
export async function retryFacultyPayout(
  payoutId: string
): Promise<{ processed: boolean; error?: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty-payouts/${payoutId}/retry`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to retry faculty payout');
  }

  return result.data;
}

/**
 * Thrown by issueFacultyPayment on a 409 duplicate guard — the caller can
 * re-submit with force: true after the admin confirms.
 */
export class DuplicatePaymentWarning extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicatePaymentWarning';
  }
}

/**
 * SDA-221: issue a one-off payment (reimbursement/bonus/other) to a faculty
 * member. Processes the Stripe transfer immediately; a `processed: false`
 * response means the row was recorded but the transfer failed (see `error`).
 */
export async function issueFacultyPayment(
  facultyId: string,
  data: {
    amount_cents: number;
    payment_type: 'reimbursement' | 'bonus' | 'other';
    note: string;
    force?: boolean;
  }
): Promise<{ payment: { id: string; amount_cents: number; status: string }; processed: boolean; error?: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty/${facultyId}/issue-payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!result.success) {
    const message = result.error?.message || 'Failed to issue payment';
    if (response.status === 409) {
      throw new DuplicatePaymentWarning(message);
    }
    throw new Error(message);
  }

  return result.data;
}

export async function cancelFacultyPayout(payoutId: string): Promise<FacultyPayoutAdmin> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty-payouts/${payoutId}/cancel`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to cancel faculty payout');
  }

  return result.data;
}

export interface PayoutForecast {
  pending_payouts: Array<{
    id: string;
    amount_cents: number;
    scheduled_for: string;
    created_at: string;
    booking_id: string | null;
    booking_number: string | null;
    faculty_name: string | null;
    class_date: string | null;
    show_name: string | null;
    studio_name: string | null;
    payment_type: string;
    note: string | null;
  }>;
  upcoming_classes: Array<{
    class_id: string;
    class_date: string;
    status: string;
    booking_id: string | null;
    booking_number: string | null;
    faculty_name: string | null;
    show_name: string | null;
    studio_name: string | null;
    expected_amount_cents: number;
  }>;
  total_pending_cents: number;
  total_forecast_cents: number;
}

export async function getPayoutForecast(): Promise<PayoutForecast> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/faculty-payouts/forecast`);
  const result = await response.json();
  if (!result.success) throw new Error(result.error?.message || 'Failed to fetch forecast');
  return result.data as PayoutForecast;
}

// =============================================================================
// Types - Admin Class Search
// =============================================================================

export interface AdminClassSearchResult {
  id: string;
  class_date: string;
  start_time: string;
  status: string;
  faculty_name: string | null;
  show_name: string | null;
  studio_name: string | null;
  booking_number: string | null;
  faculty_rate_cents: number | null;
  faculty_paid: boolean;
}

// =============================================================================
// API Functions - Admin Class Search
// =============================================================================

export async function searchAdminClasses(params?: {
  search?: string;
  has_faculty?: boolean;
  faculty_paid?: boolean;
  page?: number;
  per_page?: number;
}): Promise<{ classes: AdminClassSearchResult[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.has_faculty !== undefined) query.set('has_faculty', String(params.has_faculty));
  if (params?.faculty_paid !== undefined) query.set('faculty_paid', String(params.faculty_paid));
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));
  const url = query.toString() ? `${API_BASE}/api/admin/classes?${query}` : `${API_BASE}/api/admin/classes`;
  const response = await authenticatedFetch(url);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to search classes');
  return result.data;
}

// Booking aliases
export type Booking = AdminBooking;
export type BookingNote = AdminNote;

// Inquiry aliases
export type Inquiry = AdminInquiry;

// Faculty aliases
export type Faculty = AdminFaculty;

// =============================================================================
// Function Aliases (for page compatibility)
// =============================================================================

// Booking function aliases
export const getBooking = getAdminBooking;
export const getBookings = getAdminBookings;
export const updateBooking = updateAdminBooking;
export const processRefund = refundBooking;

// Inquiry function aliases
export const getInquiries = getAdminInquiries;
export const getInquiry = getAdminInquiry;

// =============================================================================
// Types - Email Accounts
// =============================================================================

export interface EmailAccount {
  id: string;
  email: string;
  provider: string;
  display_name: string | null;
  role: string;
  is_active: boolean;
  connected_at: string;
  /**
   * Result of the last Google login check (8 AM ET daily, or "Check now").
   * null/absent = not checked yet; 'unknown' = Google itself was erroring.
   */
  health_status?: EmailAccountHealthStatus | null;
  health_checked_at?: string | null;
  /** When the check first saw the login rejected; cleared once it works again. */
  health_failing_since?: string | null;
  /** Google's error text (never a token). */
  health_error?: string | null;
}

export type EmailAccountHealthStatus = 'ok' | 'failing' | 'unknown';

/** One account's result from POST /email-accounts/health-check. */
export interface EmailAccountHealth {
  id: string;
  email: string;
  role: string;
  display_name: string | null;
  health_status: EmailAccountHealthStatus;
  health_checked_at: string;
  health_failing_since: string | null;
  health_error: string | null;
}

// =============================================================================
// API - Email Accounts
// =============================================================================

export async function getEmailAccounts(): Promise<EmailAccount[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts`);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to get email accounts');
  return (result.data as EmailAccount[]) || [];
}

/** @deprecated Use getEmailAccounts() instead */
export async function getEmailAccount(): Promise<EmailAccount | null> {
  const accounts = await getEmailAccounts();
  return accounts.find((a) => a.role === 'system') ?? accounts[0] ?? null;
}

export async function getEmailAuthUrl(role: string = 'system'): Promise<{ url: string; state: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/auth-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to get auth URL');
  return { url: result.data.auth_url, state: result.data.state };
}

export async function connectEmailAccount(code: string, state: string, role: string = 'system'): Promise<EmailAccount> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state, role }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to connect email account');
  return result.data;
}

export async function disconnectEmailAccount(id: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to disconnect email account');
  }
}

export async function updateEmailAccountDisplayName(id: string, displayName: string): Promise<EmailAccount> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: displayName }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to update display name');
  return result.data;
}

/** Send a test email to the signed-in admin FROM the active account of this role. */
export async function sendTestEmail(
  role: 'system' | 'event_manager' = 'system'
): Promise<{ sent_to: string; from: string; role: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to send test email');
  return result.data;
}

/** "Check now": ask Google whether each connected account's login still works. */
export async function runEmailHealthCheck(): Promise<EmailAccountHealth[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/email-accounts/health-check`, {
    method: 'POST',
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to check email accounts');
  return (result.data as EmailAccountHealth[]) || [];
}

// =============================================================================
// API Functions - Admin Invitations (super-admin allowlist gates these)
// =============================================================================

export interface AdminInvite {
  id: string;
  email: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  invited_by: { email: string; first_name: string | null; last_name: string | null } | null;
  accepted_by: { email: string; first_name: string | null; last_name: string | null } | null;
  accepted_user_id: string | null;
}

export async function getAdminInvites(): Promise<AdminInvite[]> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/admin-invites`);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to load admin invitations');
  return result.data;
}

export async function createAdminInvite(email: string): Promise<{ invite: AdminInvite; emailSent: boolean; emailWarning?: string }> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/admin-invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to create admin invitation');
  return result.data;
}

export async function revokeAdminInvite(id: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/admin-invites/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 204) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to revoke invitation');
  }
}

export async function removeAdmin(userId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/admins/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 204) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to remove admin');
  }
}
